import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Plus,
  Edit3,
  Trash2,
  RefreshCw,
  BookMarked,
  ArrowUp,
  ArrowDown,
  X,
  EyeOff,
  Store,
  Percent,
} from 'lucide-react';
import { backofficeApi, formatNumber, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  CollectionDto,
  CollectionItemRequestDto,
  CollectionRequestDto,
  CollectionStatus,
  ComboItem,
} from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  SearchInput,
  SearchableComboBox,
  SegmentedControl,
  Select,
  TextInput,
  Switch,
  Modal,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import DataState from '../components/DataState';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const TitleCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }

  span {
    font-size: 0.78rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const SectionTitle = styled.h4`
  margin: 1.5rem 0 0.5rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${(p) => p.theme.textPrimary};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 90px;
  resize: vertical;
  font: inherit;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  padding: 0.6rem 0.75rem;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const PickerRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.75rem;
  align-items: end;
  margin-top: 0.5rem;
`;

const ItemList = styled.ol`
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ItemRow = styled.li<{ $dimmed?: boolean }>`
  display: grid;
  grid-template-columns: 1.6rem 1fr auto;
  gap: 0.6rem;
  align-items: center;
  padding: 0.55rem 0.7rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => (p.$dimmed ? p.theme.surfaceAlt : p.theme.surface)};
  opacity: ${(p) => (p.$dimmed ? 0.75 : 1)};
`;

const ItemPosition = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${(p) => p.theme.textMuted};
  text-align: right;
`;

const ItemMain = styled.div`
  min-width: 0;

  strong {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.85rem;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const ItemActions = styled.div`
  display: flex;
  gap: 0.3rem;
`;

const EmptyItems = styled.p`
  margin: 0.75rem 0 0;
  padding: 0.9rem;
  border: 1px dashed ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  font-size: 0.82rem;
  color: ${(p) => p.theme.textMuted};
  text-align: center;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const STATUS_FILTER = [
  { value: '', label: 'Todas as situações' },
  { value: 'DRAFT', label: 'Rascunho' },
  { value: 'PUBLISHED', label: 'Publicada' },
  { value: 'ARCHIVED', label: 'Arquivada' },
];

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Rascunho' },
  { value: 'PUBLISHED', label: 'Publicada' },
  { value: 'ARCHIVED', label: 'Arquivada' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * Piso de itens da regra §33.4 - repetido aqui só para avisar antes de salvar.
 * Quem decide é o backend; o que esta constante evita é o curador publicar,
 * fechar a tela e só descobrir depois que a coleção não entrou na vitrine.
 */
const MIN_ITEMS = 2;

const EMPTY_FORM: CollectionRequestDto = {
  title: '',
  status: 'DRAFT',
  localityId: null,
  subtitle: '',
  intro: '',
  coverUrl: '',
  seoTitle: '',
  seoDescription: '',
  sortOrder: 0,
  publishedFrom: null,
  publishedUntil: null,
  items: [],
};

/** Um item na tela, com o rótulo já resolvido para não recarregar o alvo. */
interface DraftItem {
  key: string;
  merchantId: string | null;
  promotionId: string | null;
  label: string;
  note: string;
  /** `false` = loja suspensa ou anúncio fora do ar; some do site, fica aqui. */
  visible: boolean;
}

type TargetKind = 'MERCHANT' | 'PROMOTION';

const TARGET_OPTIONS = [
  { value: 'MERCHANT' as TargetKind, label: 'Estabelecimento' },
  { value: 'PROMOTION' as TargetKind, label: 'Anúncio' },
];

/**
 * `datetime-local` fala "2026-08-25T18:30" e o backend fala ISO-8601 com fuso.
 * Mesmas duas funções da tela de Matérias, e pela mesma razão: sem elas o
 * formulário manda a string crua, o backend recusa e o curador perde o texto.
 */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  if (!value.trim()) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * Chave local de um item da lista.
 *
 * Não é o id do alvo: o curador pode colocar a mesma loja duas vezes com
 * comentários diferentes, e com a chave pelo alvo o React embaralharia as duas
 * linhas ao reordenar.
 */
let draftSequence = 0;
function nextKey(): string {
  draftSequence += 1;
  return `item-${draftSequence}`;
}

const CollectionsPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [cityFilter, setCityFilter] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CollectionRequestDto>(EMPTY_FORM);
  const [items, setItems] = useState<DraftItem[]>([]);
  const [publishWindow, setPublishWindow] = useState({ from: '', until: '' });
  const [targetKind, setTargetKind] = useState<TargetKind>('MERCHANT');
  const [targetId, setTargetId] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<CollectionDto | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);

  const debouncedSearch = useDebouncedValue(search.trim());

  const { data, loading, error, reload } = useAsync(
    () =>
      backofficeApi.listCollections({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
        status: (statusFilter || undefined) as CollectionStatus | undefined,
        localityId: cityFilter || undefined,
        sort: 'sortOrder,asc',
      }),
    [page, pageSize, debouncedSearch, statusFilter, cityFilter]
  );

  const { data: localityPage } = useAsync(
    () => backofficeApi.listLocalities({ page: 0, size: 100, active: true, sort: 'name,asc' }),
    []
  );

  // O seletor de alvo lê o catálogo uma vez e filtra no cliente. É o mesmo
  // desenho do seletor de cidade: são listas de dezenas, não de milhares, e uma
  // busca no servidor a cada tecla faria a tela piscar durante a curadoria -
  // que é justamente o momento em que a pessoa está comparando opções.
  const { data: merchantPage, loading: loadingMerchants } = useAsync(
    () => backofficeApi.listMerchants({ page: 0, size: 200, status: 'ACTIVE', sort: 'name,asc' }),
    []
  );
  const { data: promotionPage, loading: loadingPromotions } = useAsync(
    () => backofficeApi.listPromotions({ page: 0, size: 200, status: 'ACTIVE' }),
    []
  );

  const cityOptions = useMemo(
    () => [
      { value: '', label: 'Todas as cidades' },
      ...(localityPage?.content ?? []).map((l) => ({ value: l.id, label: `${l.name}, ${l.state}` })),
    ],
    [localityPage]
  );

  const merchantItems: ComboItem[] = useMemo(
    () => (merchantPage?.content ?? []).map((m) => ({ id: m.id, label: m.tradeName || m.name })),
    [merchantPage]
  );

  const promotionItems: ComboItem[] = useMemo(
    () =>
      (promotionPage?.content ?? []).map((p) => ({
        id: p.id,
        label: p.merchantName ? `${p.title} - ${p.merchantName}` : p.title,
      })),
    [promotionPage]
  );

  const collections = data?.content ?? [];
  const totalItems = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const resetToFirstPage = (): void => setPage(0);

  const openCreate = (): void => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setItems([]);
    setPublishWindow({ from: '', until: '' });
    setTargetId('');
    setIsOpen(true);
  };

  const openEdit = (collection: CollectionDto): void => {
    setEditingId(collection.id);
    setForm({
      title: collection.title,
      status: collection.status,
      localityId: collection.localityId,
      subtitle: collection.subtitle ?? '',
      intro: collection.intro ?? '',
      coverUrl: collection.coverUrl ?? '',
      seoTitle: collection.seoTitle ?? '',
      seoDescription: collection.seoDescription ?? '',
      sortOrder: collection.sortOrder,
      publishedFrom: collection.publishedFrom,
      publishedUntil: collection.publishedUntil,
      items: [],
    });
    setItems(
      collection.items.map((item) => ({
        key: nextKey(),
        merchantId: item.merchantId,
        promotionId: item.promotionId,
        label: item.merchantName ?? item.promotionTitle ?? 'Item removido',
        note: item.note ?? '',
        visible: item.visible,
      }))
    );
    setPublishWindow({
      from: toLocalInput(collection.publishedFrom),
      until: toLocalInput(collection.publishedUntil),
    });
    setTargetId('');
    setIsOpen(true);
  };

  const update = <K extends keyof CollectionRequestDto>(
    key: K,
    value: CollectionRequestDto[K]
  ): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addTarget = (): void => {
    if (!targetId) return;
    const source = targetKind === 'MERCHANT' ? merchantItems : promotionItems;
    const chosen = source.find((option) => option.id === targetId);
    if (!chosen) return;
    setItems((prev) => [
      ...prev,
      {
        key: nextKey(),
        merchantId: targetKind === 'MERCHANT' ? chosen.id : null,
        promotionId: targetKind === 'PROMOTION' ? chosen.id : null,
        label: chosen.label,
        note: '',
        // Só entram alvos ativos no seletor, então o item nasce visível.
        visible: true,
      },
    ]);
    setTargetId('');
  };

  const moveItem = (index: number, direction: -1 | 1): void => {
    setItems((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeItem = (key: string): void => {
    setItems((prev) => prev.filter((item) => item.key !== key));
  };

  const setNote = (key: string, note: string): void => {
    setItems((prev) => prev.map((item) => (item.key === key ? { ...item, note } : item)));
  };

  const submit = async (): Promise<void> => {
    if (!form.title.trim()) {
      showNotification('Informe o título da coleção.', 'error');
      return;
    }
    if (items.length === 0) {
      showNotification('Uma coleção sem itens não é uma lista. Acrescente pelo menos dois.', 'error');
      return;
    }

    const publishedFrom = fromLocalInput(publishWindow.from);
    const publishedUntil = fromLocalInput(publishWindow.until);
    // O backend recusa com COLLECTION_INVALID_WINDOW; avisar aqui poupa a ida e
    // volta, porque janela invertida é uma coleção que nunca aparece.
    if (publishedFrom && publishedUntil && new Date(publishedUntil) < new Date(publishedFrom)) {
      showNotification('O fim da publicação deve ser depois do início.', 'error');
      return;
    }

    const visibleCount = items.filter((item) => item.visible).length;
    if (form.status === 'PUBLISHED' && visibleCount < MIN_ITEMS) {
      showNotification(
        `Com menos de ${MIN_ITEMS} itens no ar a coleção não entra na vitrine nem no sitemap - só responde pelo link direto.`,
        'warning'
      );
    }

    setSubmitting(true);
    try {
      const payload: CollectionRequestDto = {
        ...form,
        title: form.title.trim(),
        localityId: form.localityId || null,
        publishedFrom,
        publishedUntil,
        // A ordem é a posição no array - é isso que o backend grava.
        items: items.map<CollectionItemRequestDto>((item) => ({
          merchantId: item.merchantId,
          promotionId: item.promotionId,
          note: item.note.trim() || null,
        })),
      };
      if (editingId) {
        await backofficeApi.updateCollection(editingId, payload);
        showNotification('Coleção atualizada com sucesso.', 'success');
      } else {
        await backofficeApi.createCollection(payload);
        showNotification('Coleção criada com sucesso.', 'success');
      }
      setIsOpen(false);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar a coleção.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /** Publicar/despublicar na própria linha, como em Matérias e Localidades. */
  const togglePublished = async (collection: CollectionDto): Promise<void> => {
    const nextStatus: CollectionStatus = collection.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    try {
      await backofficeApi.updateCollection(collection.id, {
        title: collection.title,
        status: nextStatus,
        localityId: collection.localityId,
        subtitle: collection.subtitle,
        intro: collection.intro,
        coverUrl: collection.coverUrl,
        seoTitle: collection.seoTitle,
        seoDescription: collection.seoDescription,
        sortOrder: collection.sortOrder,
        publishedFrom: collection.publishedFrom,
        publishedUntil: collection.publishedUntil,
        // A lista tem que ir junto: o payload substitui os itens, e mandá-lo sem
        // eles esvaziaria a coleção ao clicar no botão de publicar.
        items: collection.items.map<CollectionItemRequestDto>((item) => ({
          merchantId: item.merchantId,
          promotionId: item.promotionId,
          note: item.note,
        })),
      });
      showNotification(
        nextStatus === 'PUBLISHED' ? 'Coleção publicada.' : 'Coleção voltou para rascunho.',
        'success'
      );
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível alterar a situação.'), 'error');
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteCollection(deleting.id);
      showNotification('Coleção excluída.', 'success');
      setDeleting(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível excluir a coleção.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  const hiddenCount = items.filter((item) => !item.visible).length;

  return (
    <>
      <PageHeader
        title="Coleções"
        subtitle="Listas curadas à mão, com endereço próprio em /colecoes. Sem cidade, a coleção vale para todas."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Nova coleção
          </Button>
        }
      />

      <StatsGrid>
        <StatCard label="Total (respeita o filtro)" value={formatNumber(totalItems)} />
        <StatCard label="No ar (nesta página)" value={formatNumber(collections.filter((c) => c.visible).length)} />
        <StatCard
          label="Rascunhos (nesta página)"
          value={formatNumber(collections.filter((c) => c.status === 'DRAFT').length)}
        />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por título ou endereço..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetToFirstPage();
          }}
        />
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            resetToFirstPage();
          }}
        />
        <Select
          options={cityOptions}
          value={cityFilter}
          onChange={(e) => {
            setCityFilter(e.target.value);
            resetToFirstPage();
          }}
        />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={collections.length === 0}
        emptyTitle="Nenhuma coleção encontrada"
        emptyDescription="Monte a primeira lista curada - é o conteúdo que rende link no WhatsApp e no Instagram."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Coleção</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Onde aparece</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>No ar</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {collections.map((collection) => {
                const visibleItems = collection.items.filter((item) => item.visible).length;
                return (
                  <tr key={collection.id}>
                    <td>
                      <TitleCell>
                        <BookMarked size={14} />
                        <div>
                          <strong>{collection.title}</strong>
                          <br />
                          <span>/colecoes/{collection.slug}</span>
                        </div>
                      </TitleCell>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{collection.localityLabel ?? 'Todas as cidades'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {/* Itens no ar / itens na lista. Quando os dois números
                          divergem, alguma loja da curadoria saiu do ar. */}
                      <Badge tone={visibleItems < MIN_ITEMS ? 'warning' : 'secondary'}>
                        {visibleItems}/{collection.items.length}
                      </Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Badge
                        tone={
                          collection.visible
                            ? 'success'
                            : collection.status === 'PUBLISHED'
                              ? 'warning'
                              : 'secondary'
                        }
                      >
                        {collection.visible
                          ? 'No ar'
                          : collection.status === 'PUBLISHED'
                            ? 'Agendada'
                            : 'Rascunho'}
                      </Badge>
                    </td>
                    <td>
                      <RowActions>
                        <Switch
                          checked={collection.status === 'PUBLISHED'}
                          onChange={() => togglePublished(collection)}
                          aria-label={
                            collection.status === 'PUBLISHED' ? 'Voltar para rascunho' : 'Publicar coleção'
                          }
                        />
                        <ActionButton aria-label="Editar coleção" title="Editar" onClick={() => openEdit(collection)}>
                          <Edit3 size={16} />
                        </ActionButton>
                        <ActionButton
                          aria-label="Excluir coleção"
                          title="Excluir"
                          color="#DC2626"
                          onClick={() => setDeleting(collection)}
                        >
                          <Trash2 size={16} />
                        </ActionButton>
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {totalPages > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            resetToFirstPage();
          }}
        />
      )}

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editingId ? 'Editar coleção' : 'Nova coleção'}
        width="760px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        <TextInput
          label="Título"
          value={form.title}
          onChange={(e) => update('title', e.target.value)}
          placeholder="Ex.: 10 lugares para conhecer em Toledo"
        />
        <TextInput
          label="Subtítulo"
          value={form.subtitle ?? ''}
          onChange={(e) => update('subtitle', e.target.value)}
          placeholder="Uma linha que explica a lista"
          containerStyle={{ marginTop: '0.75rem' }}
        />

        <div style={{ marginTop: '0.75rem' }}>
          <FieldLabel htmlFor="collection-intro">Texto de abertura</FieldLabel>
          <TextArea
            id="collection-intro"
            value={form.intro ?? ''}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => update('intro', e.target.value)}
            placeholder="Opcional. Um parágrafo dizendo por que esta lista existe."
          />
        </div>

        <FieldRow>
          <Select
            label="Cidade"
            options={cityOptions}
            value={form.localityId ?? ''}
            onChange={(e) => update('localityId', e.target.value || null)}
          />
          <Select
            label="Situação"
            options={STATUS_OPTIONS}
            value={form.status}
            onChange={(e) => update('status', e.target.value as CollectionStatus)}
          />
        </FieldRow>
        <FieldHint>Sem cidade a coleção aparece em todas as cidades.</FieldHint>

        <SectionTitle>Itens da lista</SectionTitle>
        <FieldHint>
          A ordem aqui é a ordem que o visitante vê - é ela que diferencia uma coleção de um filtro
          salvo. Abaixo de {MIN_ITEMS} itens no ar a coleção só responde pelo link direto: não entra
          na vitrine nem no sitemap.
        </FieldHint>

        <PickerRow>
          <div>
            <FieldLabel as="span">Tipo</FieldLabel>
            <SegmentedControl
              value={targetKind}
              onChange={(value) => {
                setTargetKind(value);
                setTargetId('');
              }}
              options={TARGET_OPTIONS}
            />
          </div>
          <SearchableComboBox
            label={targetKind === 'MERCHANT' ? 'Estabelecimento' : 'Anúncio'}
            items={targetKind === 'MERCHANT' ? merchantItems : promotionItems}
            valueId={targetId}
            onChange={setTargetId}
            loading={targetKind === 'MERCHANT' ? loadingMerchants : loadingPromotions}
            placeholder="Buscar pelo nome..."
            noResultsText="Nada encontrado com esse nome"
          />
          <Button variant="secondary" onClick={addTarget} disabled={!targetId}>
            <Plus size={16} /> Acrescentar
          </Button>
        </PickerRow>

        {items.length === 0 ? (
          <EmptyItems>Nenhum item ainda. Acrescente os lugares que esta lista recomenda.</EmptyItems>
        ) : (
          <ItemList>
            {items.map((item, index) => (
              <ItemRow key={item.key} $dimmed={!item.visible}>
                <ItemPosition>{index + 1}.</ItemPosition>
                <ItemMain>
                  <strong>
                    {item.merchantId ? <Store size={13} /> : <Percent size={13} />}
                    {item.label}
                    {!item.visible && (
                      <Badge tone="warning">
                        <EyeOff size={11} /> Fora do ar
                      </Badge>
                    )}
                  </strong>
                  <TextInput
                    value={item.note}
                    onChange={(e) => setNote(item.key, e.target.value)}
                    placeholder="Por que este lugar está na lista (opcional)"
                    containerStyle={{ marginTop: '0.4rem' }}
                  />
                </ItemMain>
                <ItemActions>
                  <ActionButton
                    aria-label="Subir item"
                    title="Subir"
                    disabled={index === 0}
                    onClick={() => moveItem(index, -1)}
                  >
                    <ArrowUp size={15} />
                  </ActionButton>
                  <ActionButton
                    aria-label="Descer item"
                    title="Descer"
                    disabled={index === items.length - 1}
                    onClick={() => moveItem(index, 1)}
                  >
                    <ArrowDown size={15} />
                  </ActionButton>
                  <ActionButton
                    aria-label="Remover item"
                    title="Remover"
                    color="#DC2626"
                    onClick={() => removeItem(item.key)}
                  >
                    <X size={15} />
                  </ActionButton>
                </ItemActions>
              </ItemRow>
            ))}
          </ItemList>
        )}

        {hiddenCount > 0 && (
          <FieldHint>
            {hiddenCount === 1
              ? '1 item está fora do ar e não aparece na página pública.'
              : `${hiddenCount} itens estão fora do ar e não aparecem na página pública.`}{' '}
            Eles continuam aqui de propósito: se a loja voltar, o item volta com ela.
          </FieldHint>
        )}

        <SectionTitle>Publicação e busca</SectionTitle>
        <FieldRow>
          <TextInput
            label="Publicar a partir de"
            type="datetime-local"
            value={publishWindow.from}
            onChange={(e) => setPublishWindow((prev) => ({ ...prev, from: e.target.value }))}
          />
          <TextInput
            label="Publicar até"
            type="datetime-local"
            value={publishWindow.until}
            onChange={(e) => setPublishWindow((prev) => ({ ...prev, until: e.target.value }))}
          />
        </FieldRow>
        <FieldHint>
          Em branco significa sem limite. Publicada com início no futuro, a coleção fica agendada e
          não aparece no site antes da data.
        </FieldHint>

        <FieldRow>
          <TextInput
            label="Título de SEO"
            value={form.seoTitle ?? ''}
            onChange={(e) => update('seoTitle', e.target.value)}
            placeholder="Cai para o título quando vazio"
          />
          <TextInput
            label="Ordem"
            type="number"
            value={String(form.sortOrder ?? 0)}
            onChange={(e) => update('sortOrder', Number(e.target.value) || 0)}
          />
        </FieldRow>
        <TextInput
          label="Descrição de SEO"
          value={form.seoDescription ?? ''}
          onChange={(e) => update('seoDescription', e.target.value)}
          placeholder="Resumo que aparece no Google e na prévia do WhatsApp"
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <TextInput
          label="Imagem de capa (URL)"
          value={form.coverUrl ?? ''}
          onChange={(e) => update('coverUrl', e.target.value)}
          placeholder="https://..."
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <FieldHint>
          A capa é a imagem da prévia quando alguém manda o link no WhatsApp. Sem ela, a prévia usa
          o logotipo do Kardume.
        </FieldHint>
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Excluir coleção"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemove} isLoading={removing}>
              Excluir
            </Button>
          </>
        }
      >
        <ConfirmText>
          Tem certeza que deseja excluir <strong>{deleting?.title ?? ''}</strong>? O endereço{' '}
          <strong>/colecoes/{deleting?.slug ?? ''}</strong> deixará de responder, e todo link já
          compartilhado dele vira 404.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default CollectionsPage;
